import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'

interface ContactDetail {
  id?: string
  type: string
  label: string
  value: string
  show_in_contact_page: boolean
  show_in_footer: boolean
  is_active: boolean
  display_order: number
  icon_name?: string
  custom_icon_url?: string
}

interface ContactDetailsFormProps {
  contactDetail?: ContactDetail
  onSave: () => void
  onCancel: () => void
}

const ContactDetailsForm = ({ contactDetail, onSave, onCancel }: ContactDetailsFormProps) => {
  const { toast } = useToast()
  const [formData, setFormData] = useState<ContactDetail>({
    type: 'email',
    label: '',
    value: '',
    show_in_contact_page: true,
    show_in_footer: true,
    is_active: true,
    display_order: 0,
    icon_name: '',
    custom_icon_url: '',
    ...contactDetail
  })
  const [iconType, setIconType] = useState<'none' | 'lucide' | 'custom'>(
    contactDetail?.custom_icon_url ? 'custom' : 
    contactDetail?.icon_name ? 'lucide' : 'none'
  )
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Clean up icon fields based on iconType
      const cleanedFormData = {
        ...formData,
        icon_name: iconType === 'lucide' ? formData.icon_name : null,
        custom_icon_url: iconType === 'custom' ? formData.custom_icon_url : null
      }

      if (contactDetail?.id) {
        // Update existing contact detail
        const { error } = await supabase
          .from('contact_details')
          .update(cleanedFormData)
          .eq('id', contactDetail.id)

        if (error) throw error

        toast({
          title: "Success",
          description: "Contact detail updated successfully!",
        })
      } else {
        // Create new contact detail
        const { error } = await supabase
          .from('contact_details')
          .insert([cleanedFormData])

        if (error) throw error

        toast({
          title: "Success",
          description: "Contact detail created successfully!",
        })
      }

      onSave()
    } catch (error) {
      console.error('Error saving contact detail:', error)
      toast({
        title: "Error",
        description: "Failed to save contact detail. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="type">Type</Label>
          <Select
            value={formData.type}
            onValueChange={(value) => setFormData({ ...formData, type: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="address">Address</SelectItem>
              <SelectItem value="social">Social Media</SelectItem>
              <SelectItem value="note">Note</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="display_order">Display Order</Label>
          <Input
            id="display_order"
            type="number"
            value={formData.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="label">Label</Label>
        <Input
          id="label"
          type="text"
          value={formData.label}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          placeholder="e.g., Primary Email, Main Phone"
          required
        />
      </div>

      <div>
        <Label htmlFor="value">Value</Label>
        <Input
          id="value"
          type="text"
          value={formData.value}
          onChange={(e) => setFormData({ ...formData, value: e.target.value })}
          placeholder="e.g., contact@company.com, +1 234 567 8900"
          required
        />
      </div>

      <div className="space-y-3">
        <Label className="text-base font-medium">Icon</Label>
        
        <div className="flex gap-4">
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="no-icon"
              name="iconType"
              checked={iconType === 'none'}
              onChange={() => setIconType('none')}
            />
            <Label htmlFor="no-icon">No Icon</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="lucide-icon"
              name="iconType"
              checked={iconType === 'lucide'}
              onChange={() => setIconType('lucide')}
            />
            <Label htmlFor="lucide-icon">Lucide Icon</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="custom-icon"
              name="iconType"
              checked={iconType === 'custom'}
              onChange={() => setIconType('custom')}
            />
            <Label htmlFor="custom-icon">Custom Icon URL</Label>
          </div>
        </div>

        {iconType === 'lucide' && (
          <div>
            <Label htmlFor="icon_name">Lucide Icon Name</Label>
            <Input
              id="icon_name"
              type="text"
              value={formData.icon_name || ''}
              onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
              placeholder="e.g., Mail, Phone, MapPin, Instagram"
            />
          </div>
        )}

        {iconType === 'custom' && (
          <div>
            <Label htmlFor="custom_icon_url">Custom Icon URL</Label>
            <Input
              id="custom_icon_url"
              type="url"
              value={formData.custom_icon_url || ''}
              onChange={(e) => setFormData({ ...formData, custom_icon_url: e.target.value })}
              placeholder="https://example.com/icon.svg"
            />
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Label className="text-base font-medium">Visibility</Label>
        
        <div className="flex items-center space-x-2">
          <Checkbox
            id="show_in_contact_page"
            checked={formData.show_in_contact_page}
            onCheckedChange={(checked) => 
              setFormData({ ...formData, show_in_contact_page: checked as boolean })
            }
          />
          <Label htmlFor="show_in_contact_page">Show in Contact Us page</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="show_in_footer"
            checked={formData.show_in_footer}
            onCheckedChange={(checked) => 
              setFormData({ ...formData, show_in_footer: checked as boolean })
            }
          />
          <Label htmlFor="show_in_footer">Show in Footer</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="is_active"
            checked={formData.is_active}
            onCheckedChange={(checked) => 
              setFormData({ ...formData, is_active: checked as boolean })
            }
          />
          <Label htmlFor="is_active">Active</Label>
        </div>
      </div>

      <div className="flex gap-4 pt-4">
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : contactDetail ? 'Update' : 'Create'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

export default ContactDetailsForm